import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';

import { BitbucketProjectPickerField } from './components/BitbucketProjectPickerField';
import { CloudAccountPickerField } from './components/CloudAccountPickerField';
import { DynamicRepositoryNameField } from './components/DynamicRepositoryNameField/DynamicRepositoryNameField';
import { GitHubProjectPickerField } from './components/GitHubProjectPickerField';
import { ProductAreaPickerField } from './components/ProductAreaPickerField';
import { QualityGatePickerField } from './components/QualityGatePickerField/QualityGatePickerField';
import { SystemPickerForGroup } from './components/SystemPickerForGroup';
import { VcsRepositoryNameField } from './components/VcsRepositoryNameField';
import { validateRepositoryNameUniqueness } from './components/VcsRepositoryNameField/validateRepositoryNameUniqueness';

/**
 * Dynamic Repository Name Field Extension
 *
 * This field extension provides real-time repository name construction based on:
 * - Product Area selection (determines owner prefix from group annotations)
 * - Repository Type selection
 * - Repository Purpose input
 *
 * Features:
 * - Auto-constructs repository name as user fills form
 * - Real-time validation with clear error messages
 * - Visual feedback on field status
 * - One-click auto-fill suggestion
 */
export const DynamicRepositoryNameFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'DynamicRepositoryNameField',
    component: DynamicRepositoryNameField,
    validation: validateRepositoryNameUniqueness,
  }),
);

/**
 * System Picker For Group Field Extension
 *
 * This field extension provides dynamic system filtering based on the selected Product Area:
 * - Automatically filters systems by the owner group selected in product_area
 * - Shows systems owned by the main group or its sub-groups
 * - Provides clear feedback when no group is selected or no systems are available
 * - Uses the same UI patterns as other picker components
 *
 * Features:
 * - Dynamic system filtering based on group ownership
 * - Auto-selection when only one system is available for the group
 * - Loading and error states
 * - System titles and descriptions in dropdown
 * - Handles different owner reference formats
 */
export const SystemPickerForGroupExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'SystemPickerForGroup',
    component: SystemPickerForGroup,
  }),
);

/**
 * Cloud Account Picker Field Extension
 *
 * This field extension displays and stores cloud accounts for the selected Product Area:
 * - Automatically filters cloud-account Resources by the Product Area selected in product_area
 * - Displays all cloud accounts owned by the Product Area or any of its child teams
 * - Stores the entire list of cloud accounts as a variable for use in action steps
 * - Provides clear feedback when no Product Area is selected or no cloud accounts are available
 *
 * Features:
 * - Read-only display of cloud accounts (not a user selection field)
 * - Dynamic filtering based on Product Area ownership
 * - Fetches accounts owned by Product Area and all child teams
 * - Automatically stores cloud account data in form context
 * - Shows account names, IDs, providers, and descriptions
 * - Loading and error states with graceful error handling
 * - Handles both plain name and group reference formats for owners
 *
 * Data Structure:
 * The field stores an array of simplified cloud account objects:
 * ```typescript
 * [
 *   {
 *     title: "Dev-AWS-10043-WHQ-PEDevOps",
 *     provider: "aws",
 *     accountId: "400331679889"
 *   },
 *   {
 *     title: "Stg-AWS-30012-WHQ-AHP",
 *     provider: "aws",
 *     accountId: "716953948548"
 *   },
 *   ...
 * ]
 * ```
 *
 * Usage in template.yaml:
 * ```yaml
 * cloud_accounts:
 *   title: Cloud Accounts
 *   type: array
 *   ui:field: CloudAccountPickerField
 *   description: Available cloud accounts for this Product Area
 * ```
 *
 * Accessing in action steps:
 * ```yaml
 * steps:
 *   - id: deploy-to-account
 *     action: custom:deployToAws
 *     input:
 *       # Access specific account ID (easy!)
 *       accountId: ${{ parameters.cloud_accounts[0].accountId }}
 *       
 *       # Access provider
 *       provider: ${{ parameters.cloud_accounts[0].provider }}
 *       
 *       # Pass entire accounts array
 *       allAccounts: ${{ parameters.cloud_accounts }}
 *       
 *       # Get multiple account IDs
 *       primaryAccountId: ${{ parameters.cloud_accounts[0].accountId }}
 *       secondaryAccountId: ${{ parameters.cloud_accounts[1].accountId }}
 * ```
 * 
 * Note: To hide this field from the review page (recommended for long lists),
 * add `ui:widget: hidden` to your template YAML.
 */
export const CloudAccountPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'CloudAccountPickerField',
    component: CloudAccountPickerField,
  }),
);

/**
 * Quality Gate Picker Field Extension
 *
 * This field extension loads Quality Gates dynamically from SonarCloud:
 * - Fetches available gates for the selected organization/instance
 * - Uses SonarCloud instance `github` when vcs_provider is github, otherwise `default`
 * - Resolves organization like existing templates:
 *   Bitbucket → carrier-digital (override with ui:options.bitbucketOrganization)
 *   GitHub → gh-<github_org> from the github_org form field
 * - Shows loading and error states while fetching
 * - Optional ui:options.options still overrides the dynamic list for templates that need it
 *
 * Usage in template.yaml:
 * ```yaml
 * quality_gate:
 *   title: Quality Gate
 *   type: string
 *   ui:field: QualityGatePickerField
 *   ui:options:
 *     defaultValue: "The Foundry Way"
 *     # options: [Custom Gate]   # optional static override
 * ```
 */
export const QualityGatePickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'QualityGatePickerField',
    component: QualityGatePickerField,
  }),
);

/**
 * Product Area Picker Field Extension
 *
 * This field extension provides friendly display names for Product Area selection:
 * - Shows Group titles instead of technical entity references
 * - Fetches all Groups with spec.type: product-area from Catalog API
 * - Consistent outlined styling matching other form fields
 * - Sorted alphabetically by title for better user experience
 * - Loading and error states with clear user feedback
 * - Displays descriptions as subtitles when available
 *
 * Usage in template.yaml:
 * ```yaml
 * aws_account_group:
 *   title: Product Area
 *   type: string
 *   ui:field: ProductAreaPickerField
 * ```
 */
export const ProductAreaPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ProductAreaPickerField',
    component: ProductAreaPickerField,
  }),
);

/**
 * Bitbucket Project Picker Field Extension
 *
 * This field extension provides dynamic Bitbucket Project selection:
 * - Fetches projects from Bitbucket API or Catalog based on feature flag
 * - Displays project names with project keys
 * - Consistent outlined styling matching other form fields
 * - Loading and error states with clear user feedback
 * - Supports workspace configuration via ui:options
 *
 * Usage in template.yaml:
 * ```yaml
 * bitbucket_project_key:
 *   title: Bitbucket Project
 *   type: string
 *   ui:field: BitbucketProjectPickerField
 *   ui:options:
 *     workspace: carrier-digital
 * ```
 */
export const BitbucketProjectPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'BitbucketProjectPickerField',
    component: BitbucketProjectPickerField,
  }),
);

/**
 * GitHub Organization Picker Field Extension
 *
 * Provides GitHub organization selection for scaffolder templates:
 * - Fetches organizations from GitHub API or Catalog based on feature flag
 * - Supports host configuration via ui:options
 * - Falls back to catalog on transient API errors
 *
 * Usage in template.yaml:
 * ```yaml
 * github_org:
 *   title: GitHub Organization
 *   type: string
 *   ui:field: GitHubProjectPickerField
 *   ui:options:
 *     host: github.com
 * ```
 */
export const GitHubProjectPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GitHubProjectPickerField',
    component: GitHubProjectPickerField,
  }),
);

/**
 * VCS-aware Repository Name Field Extension
 *
 * Renders the DPX owner-type-purpose name builder for both GitHub and
 * Bitbucket. Catalog uniqueness is still enforced by the field validator.
 */
export const VcsRepositoryNameFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'VcsRepositoryNameField',
    component: VcsRepositoryNameField,
    validation: validateRepositoryNameUniqueness,
  }),
);
